import { InputField } from '../Master';

export const LENGTH_DISPLAY_UNIT_MM = 1;
export const LENGTH_DISPLAY_UNIT_M = 2;
export const AREA_DISPLAY_UNIT_MM2 = 1;
export const AREA_DISPLAY_UNIT_M2 = 2;
export const DEFAULT_LENGTH_DISPLAY_UNIT = LENGTH_DISPLAY_UNIT_MM;
export const DEFAULT_AREA_DISPLAY_UNIT = AREA_DISPLAY_UNIT_M2;

// 以前の保存値。長さの単位欄にあっても自動として扱う
const LEGACY_LENGTH_UNIT = 'mm';
const SQUARE_MILLIMETERS_PER_SQUARE_METER = 1000000;
// 換算の掛け算で生じる末尾の誤差を表示から消すための有効桁数
const DISPLAY_SIGNIFICANT_DIGITS = 12;

// 自動計測項目が内部で持つ単位。表示単位の設定に関係なく長さは mm、面積は ㎡ で保存する
export function getInternalInputFieldUnit(
  unitKind: InputField['unitKind']
): string {
  if (unitKind === 'length') {
    return '㎜';
  }
  if (unitKind === 'area') {
    return '㎡';
  }
  return '';
}

// 単位欄が空か内部の単位のままなら、表示単位の設定に従って自動で表記する
export function isAutomaticInputFieldUnit(
  unitKind: InputField['unitKind'],
  currentUnit: string
): boolean {
  if (unitKind === 'length' || unitKind === 'area') {
    const isEmptyOrInternalUnit = currentUnit === '' || currentUnit === getInternalInputFieldUnit(unitKind);
    const isLegacyLengthUnit = unitKind === 'length' && currentUnit === LEGACY_LENGTH_UNIT;
    return isEmptyOrInternalUnit || isLegacyLengthUnit;
  }
  return false;
}

export function getInputFieldUnitLabel(
  unitKind: InputField['unitKind'],
  currentUnit: string,
  lengthDisplayUnit: number,
  areaDisplayUnit: number
): string {
  if (isAutomaticInputFieldUnit(unitKind, currentUnit)) {
    if (unitKind === 'length') {
      return getLengthDisplayUnitLabel(lengthDisplayUnit);
    }
    if (unitKind === 'area') {
      return getAreaDisplayUnitLabel(areaDisplayUnit);
    }
  }
  return currentUnit;
}

// 自動表記の単位は入力欄では空にして、設定に応じた単位を薄く見せる。画面の状態はこの値で持ち、内部の単位へは保存時に戻す
export function getInputFieldUnitInputValue(
  unitKind: InputField['unitKind'],
  currentUnit: string
): string {
  if (isAutomaticInputFieldUnit(unitKind, currentUnit)) {
    return '';
  }
  return currentUnit;
}

export function getStoredInputFieldUnit(
  unitKind: InputField['unitKind'],
  currentUnit: string
): string {
  if (isAutomaticInputFieldUnit(unitKind, currentUnit)) {
    return getInternalInputFieldUnit(unitKind);
  }
  return currentUnit;
}

// 表示単位の設定を変えたとき、その種類の項目に入力した単位を消して自動表記へ戻す
export function resetInputFieldUnits<
  T extends { readonly master: InputField; unit: string }
>(
  fields: readonly T[],
  unitKind: 'length' | 'area'
): T[] {
  return fields.map((field) => {
    if (field.master.unitKind === unitKind) {
      return { ...field, unit: '' };
    }
    return field;
  });
}

export function formatEverguardThresholdLabel(
  threshold: number,
  areaDisplayUnit: number
): string {
  const displayThreshold = toDisplayEverguardThreshold(threshold, areaDisplayUnit);
  const unitLabel = getAreaDisplayUnitLabel(areaDisplayUnit);
  return `${displayThreshold}${unitLabel}`;
}

export function getLengthDisplayUnitLabel(lengthDisplayUnit: number): string {
  if (lengthDisplayUnit === LENGTH_DISPLAY_UNIT_M) {
    return 'm';
  }
  return '㎜';
}

export function getAreaDisplayUnitLabel(areaDisplayUnit: number): string {
  if (areaDisplayUnit === AREA_DISPLAY_UNIT_MM2) {
    return '㎟';
  }
  return '㎡';
}

export function toDisplayEverguardThreshold(
  threshold: number,
  areaDisplayUnit: number
): number {
  if (areaDisplayUnit === AREA_DISPLAY_UNIT_MM2) {
    const squareMillimeters = threshold * SQUARE_MILLIMETERS_PER_SQUARE_METER;
    return Number(squareMillimeters.toPrecision(DISPLAY_SIGNIFICANT_DIGITS));
  }
  return threshold;
}

export function toStoredEverguardThreshold(
  threshold: number,
  areaDisplayUnit: number
): number {
  if (areaDisplayUnit === AREA_DISPLAY_UNIT_MM2) {
    return threshold / SQUARE_MILLIMETERS_PER_SQUARE_METER;
  }
  return threshold;
}
